/**
 * Editing a message after the fact — Step 55.
 *
 * Kai: *"edit everything after the fact, this includes private notes, replies
 * etc."* Correcting the record is the easy half. The rules below are the hard
 * half, and they are shared so the browser cannot offer an edit the server
 * will refuse, nor hide one it would allow.
 *
 * ── What editing a REPLY does and does not do ──────────────────────────
 *
 * A public reply has already left the building. Step 20's mailer sent it, the
 * requester has it in their inbox, and nothing here can reach into that. So
 * editing a reply changes **our record of the conversation** and not what
 * anybody received — which is worth saying out loud in the UI, because the
 * natural assumption is the opposite and acting on it ("I'll just fix that
 * wrong price") is how a customer ends up holding a promise the desk thinks
 * it never made.
 *
 * A private note never went anywhere, so editing one is unremarkable.
 *
 * ── Who may edit ───────────────────────────────────────────────────────
 *
 * The author, or a tickets admin. NOT any agent: a thread is a record of who
 * said what, and letting a colleague rewrite your words is a different
 * product from letting you correct your own. The admin exception exists
 * because the commonest real edit is redacting something that should never
 * have been typed — a password, a card number, somebody's diagnosis — and
 * that cannot wait for whoever typed it to come back off leave.
 *
 * System messages are nobody's to edit. They are the app narrating itself
 * ("Hold released"), and an edited one would be a lie told in the app's own
 * voice.
 */
export type MessageKind = 'requester_message' | 'public_reply' | 'private_note' | 'system';

export interface EditableMessage {
  kind: string;
  authorId: string | null;
}

export interface EditActor {
  id: string;
  /** Does this person hold the admin bit on the tickets resource? */
  isTicketsAdmin: boolean;
}

/** Null when the edit is allowed, else why not — in words that can be shown. */
export function messageEditError(msg: EditableMessage, actor: EditActor): string | null {
  if (msg.kind === 'system') return 'System entries cannot be edited.';
  /**
   * A requester's own words are not ours to rewrite.
   *
   * An agent "tidying" what a customer said is the one edit with no innocent
   * reading, and it is the one that matters in a dispute. Redaction is the
   * genuine need here and it belongs to an admin, who has it below.
   */
  if (msg.kind === 'requester_message' && !actor.isTicketsAdmin)
    return 'You cannot edit what the requester wrote. An administrator can redact it.';
  if (msg.authorId && msg.authorId === actor.id) return null;
  if (actor.isTicketsAdmin) return null;
  return 'Only the person who wrote it, or an administrator, can edit this.';
}

export function canEditMessage(msg: EditableMessage, actor: EditActor): boolean {
  return messageEditError(msg, actor) === null;
}

/**
 * What to warn about before the edit is saved, or null when there is nothing
 * to say. Shown in the editor, not after — a warning that arrives with the
 * confirmation is a receipt, not a warning.
 */
export function editConsequence(kind: string): string | null {
  if (kind === 'public_reply')
    return 'This was already emailed. Editing changes the record here, not the message they received.';
  if (kind === 'requester_message')
    return 'You are editing what the requester wrote. Their original is kept.';
  return null;
}
